import React, { useState, useEffect, useCallback } from 'react';
import { AxiosResponse } from 'axios';
import { Modal, Button, Table, Form, Spinner } from 'react-bootstrap';
import moment from 'moment';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import ViewVisitorRequest from './ViewVisitorRequest';

// DTOs
interface VisitorDTO {
  id: number;
  name: string;
  email: string;
  nic: string;
  phone: string;
  visitorCompany: string;
  arrivalTime: string;
  departureTime: string;
  visitorRequestId: number;
}

interface VehicleDTO {
  id: number;
  vehicleNumber: string;
  parkingSlotId: number | null;
  arrivalTime: string;
  departureTime: string;
  visitorRequestId: number;
}

interface VisitorRequestDTO {
  id: number;
  locationId: number;
  departmentId: number;
  responsiblePersonEmpNo: string;
  visitorPosition: string;
  visitorReason: string;
  parkingRequired: boolean;
  dateTime: string;
  approvedBy: string | null;
  approvedAt: string | null;
  approvalComment: string | null;
  finalApprovedBy: string | null;
  finalApprovedAt: string | null;
  finalApprovalComment: string | null;
  status: string;
  visitors: VisitorDTO[];
  vehicles: VehicleDTO[];
}

interface GetVisitorsResponse {
  status: string;
  statusDescription: string;
  data: VisitorRequestDTO[];
  total: number;
}

interface CompletedModuleProps {
  show: boolean;
  onHide: () => void;
}

const statusColors: { [key: string]: string } = {
  COMPLETED: 'badge bg-success', // Updated to match COMPLETED status
};

const CompletedModule: React.FC<CompletedModuleProps> = ({ show, onHide }) => {
  const [visitorRequests, setVisitorRequests] = useState<VisitorRequestDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pageNo, setPageNo] = useState(1);
  const [total, setTotal] = useState(0);
  const [dateFilter, setDateFilter] = useState('');
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedVisitorRequestId, setSelectedVisitorRequestId] = useState<number | null>(null);

  const itemsPerPage = 15;

  const fetchVisitorRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = { filterType: 'STATUS', filterValue: 'COMPLETED', date: dateFilter || '' };
      const res: AxiosResponse<GetVisitorsResponse> = await apiDefinitions.getVisitors('all', payload);

      if (res.status === 200 && (res.data.status === 'SUCCESS' || res.data.status === 'S1000')) {
        let completed = res.data.data || [];

        // Apply date filter if provided
        if (dateFilter) {
          const start = new Date(dateFilter);
          start.setHours(0, 0, 0, 0);
          const end = new Date(dateFilter);
          end.setHours(23, 59, 59, 999);
          completed = completed.filter((r) => {
            const dt = new Date(r.dateTime);
            return dt >= start && dt <= end;
          });
        }

        setVisitorRequests(completed);
        setTotal(completed.length);
      } else {
        setError(res.data?.statusDescription || 'Failed to fetch completed visitor requests');
      }
    } catch (err: any) {
      setError('Failed to fetch completed visitor requests. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [dateFilter]);

  useEffect(() => {
    if (show) {
      setPageNo(1); // Reset to page 1 when modal opens
      fetchVisitorRequests();
    }
  }, [show, dateFilter, fetchVisitorRequests]);

  const handlePageChange = (newPage: number) => {
    const totalPages = Math.ceil(total / itemsPerPage);
    if (newPage > 0 && newPage <= totalPages) {
      setPageNo(newPage);
    }
  };

  const handleDateFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateFilter(e.target.value);
    setPageNo(1); // Reset to page 1 on date change
  };

  const handleViewClick = (visitorRequestId: number) => {
    setSelectedVisitorRequestId(visitorRequestId);
    setShowViewModal(true);
  };

  const paginatedRequests = visitorRequests.slice((pageNo - 1) * itemsPerPage, pageNo * itemsPerPage);

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Completed Visitor Requests</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row mb-3">
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Filter by Date</Form.Label>
                <Form.Control
                  type="date"
                  value={dateFilter}
                  onChange={handleDateFilterChange}
                  disabled={loading}
                />
              </Form.Group>
            </div>
          </div>

          {loading && (
            <div className="text-center">
              <Spinner animation="border" size="sm" /> Loading...
            </div>
          )}
          {error && <div className="alert alert-danger">{error}</div>}
          {!loading && !error && visitorRequests.length === 0 && (
            <div className="alert alert-info">No completed visitor requests found.</div>
          )}

          {!loading && !error && visitorRequests.length > 0 && (
            <div className="table-responsive">
              <Table className="table text-nowrap">
                <thead className="table-primary">
                  <tr>
                    <th>ID</th>
                    <th>Visitor Name</th>
                    <th>Company</th>
                    <th>Arrival Time</th>
                    <th>Status</th>
                    <th>Created Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRequests.map((request) => (
                    <tr key={request.id}>
                      <td>{request.id}</td>
                      <td>{request.visitors[0]?.name || 'N/A'}</td>
                      <td>{request.visitors[0]?.visitorCompany || 'N/A'}</td>
                      <td>
                        {request.visitors[0]?.arrivalTime
                          ? moment(request.visitors[0].arrivalTime).format('YYYY-MM-DD HH:mm')
                          : 'N/A'}
                      </td>
                      <td>
                        <span className={statusColors[request.status] || 'badge bg-secondary'}>
                          {request.status || 'N/A'}
                        </span>
                      </td>
                      <td>{moment(request.dateTime).format('YYYY-MM-DD HH:mm')}</td>
                      <td>
                        <div className="hstack gap-2">
                          <Button
                            variant="icon btn-secondary btn-sm"
                            title="View Request"
                            onClick={() => handleViewClick(request.id)}
                          >
                            <i className="ti ti-eye" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          <div className="d-flex justify-content-between mt-3">
            <Button
              variant="outline-primary"
              disabled={pageNo === 1 || loading}
              onClick={() => handlePageChange(pageNo - 1)}
            >
              Previous
            </Button>
            <span>
              Page {pageNo} of {Math.ceil(total / itemsPerPage) || 1}
            </span>
            <Button
              variant="outline-primary"
              disabled={pageNo >= Math.ceil(total / itemsPerPage) || loading}
              onClick={() => handlePageChange(pageNo + 1)}
            >
              Next
            </Button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={onHide} disabled={loading}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* View Modal */}
      {selectedVisitorRequestId && (
        <ViewVisitorRequest
          show={showViewModal}
          onHide={() => setShowViewModal(false)}
          visitorRequestId={selectedVisitorRequestId}
        />
      )}
    </>
  );
};

export default CompletedModule;